import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  loadActivity,
  deleteExpenseFn,
  loadMoreActivityFn,
  PAGE_SIZE,
} from "@/lib/server-fns/expenses";
import type { ActivityFilters, ActivitySortBy } from "@/lib/server-fns/expenses";
import { deleteSettlementFn } from "@/lib/server-fns/settlements";
import { formatDate, formatAmount, parseBalanceStatus } from "@/lib/format";
import type { ExpenseView, SettlementView, ActivityItem } from "@/lib/types";
import {
  Container,
  Heading,
  Button,
  IconButton,
  Table,
  HStack,
  Card,
  Text,
  VStack,
  AvatarGroup,
  Avatar,
  Box,
  Flex,
  Badge,
  Tabs,
  Separator,
  Spinner,
  Input,
  NativeSelect,
} from "@chakra-ui/react";
import { MenuRoot, MenuTrigger, MenuContent, MenuItem } from "@/components/ui/menu";
import { Tooltip } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/user-avatar";
import { UserNameCell } from "@/components/user-name-cell";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { showErrorToast } from "@/lib/toast-utils";
import { getUser, getDisplayName } from "@/lib/user-utils";
import type { UserLookupContext } from "@/lib/user-utils";
import { SUPPORTED_CURRENCIES } from "@/lib/constants";
import {
  LuPlus,
  LuArrowLeftRight,
  LuReceipt,
  LuEllipsis,
  LuX,
  LuSlidersHorizontal,
  LuTrash2,
} from "react-icons/lu";
import { AddExpenseButton } from "@/components/add-expense-button";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";

export const Route = createFileRoute("/_authenticated/expenses/")({
  loader: () => loadActivity(),
  component: Activity,
});

// ── Balances Summary ──────────────────────────────────────────────────

function BalancesSummary({
  balances,
  totals,
  currentUserId,
  ctx,
}: {
  balances: { userId: string; amount: string; currency: string }[];
  totals: { userId: string; amount: string; currency: string }[];
  currentUserId: string;
  ctx: UserLookupContext;
}) {
  if (totals.length === 0 && balances.length === 0) return null;

  return (
    <Card.Root variant="outline" mb="4">
      <Card.Body p={{ base: "3", md: "4" }}>
        {totals.length > 0 && (
          <>
            <Text fontSize="sm" fontWeight="medium" color="fg.muted" mb="2">
              Overall
            </Text>
            <Flex gap={{ base: "2", md: "4" }} wrap="wrap" mb={balances.length > 0 ? "3" : "0"}>
              {totals.map((total, index) => {
                const { colorPalette, status } = parseBalanceStatus(total.amount);
                return (
                  <Badge key={index} size="lg" variant="subtle" colorPalette={colorPalette}>
                    {status === "positive"
                      ? "You are owed "
                      : status === "zero"
                        ? "Settled up "
                        : "You owe "}
                    {formatAmount(total.amount, total.currency)} {total.currency}
                  </Badge>
                );
              })}
            </Flex>
          </>
        )}

        {balances.length > 0 && (
          <>
            {totals.length > 0 && <Separator mb="3" />}
            {/* Desktop table */}
            <Box hideBelow="md">
              <Table.Root size="sm">
                <Table.Body>
                  {balances.map((balance, index) => {
                    const { colorPalette, num, status } = parseBalanceStatus(balance.amount);
                    const user = getUser(balance.userId, ctx);
                    const identifier = user.email || balance.userId;

                    return (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <UserNameCell
                            name={user.name}
                            identifier={identifier}
                            displayName={getDisplayName(balance.userId, ctx)}
                          />
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          <Text
                            fontWeight="medium"
                            fontSize="sm"
                            color={
                              status === "zero"
                                ? "fg.muted"
                                : status === "positive"
                                  ? "green.fg"
                                  : "red.fg"
                            }
                          >
                            {num > 0 ? "+" : ""}
                            {formatAmount(balance.amount, balance.currency)} {balance.currency}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge variant="subtle" colorPalette={colorPalette} size="sm">
                            {status === "zero"
                              ? "Settled"
                              : status === "positive"
                                ? "Owes you"
                                : "You owe"}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          {status !== "zero" && (
                            <Button asChild size="xs" variant="outline">
                              <Link
                                to="/settlements/new"
                                search={{
                                  fromUserId:
                                    status === "positive" ? balance.userId : currentUserId,
                                  toUserId: status === "positive" ? currentUserId : balance.userId,
                                  amount: Math.abs(num).toString(),
                                  currency: balance.currency,
                                }}
                              >
                                Settle Up
                              </Link>
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Mobile stacked */}
            <VStack gap="3" align="stretch" hideFrom="md">
              {balances.map((balance, index) => {
                const { colorPalette, num, status } = parseBalanceStatus(balance.amount);
                const user = getUser(balance.userId, ctx);
                const identifier = user.email || balance.userId;

                return (
                  <Box key={index}>
                    {index > 0 && <Separator mb="3" />}
                    <Flex justify="space-between" align="center">
                      <Box minW="0" flex="1">
                        <UserNameCell
                          name={user.name}
                          identifier={identifier}
                          displayName={getDisplayName(balance.userId, ctx)}
                          truncate
                        />
                      </Box>
                      <VStack gap="1" align="end" flexShrink={0} ml="3">
                        <Text
                          fontWeight="medium"
                          fontSize="sm"
                          color={
                            status === "zero"
                              ? "fg.muted"
                              : status === "positive"
                                ? "green.fg"
                                : "red.fg"
                          }
                        >
                          {num > 0 ? "+" : ""}
                          {formatAmount(balance.amount, balance.currency)} {balance.currency}
                        </Text>
                        <Badge variant="subtle" size="sm" colorPalette={colorPalette}>
                          {status === "zero"
                            ? "Settled"
                            : status === "positive"
                              ? "Owes you"
                              : "You owe"}
                        </Badge>
                      </VStack>
                    </Flex>
                    {status !== "zero" && (
                      <Button asChild size="sm" variant="outline" width="full" mt="2">
                        <Link
                          to="/settlements/new"
                          search={{
                            fromUserId: status === "positive" ? balance.userId : currentUserId,
                            toUserId: status === "positive" ? currentUserId : balance.userId,
                            amount: Math.abs(num).toString(),
                            currency: balance.currency,
                          }}
                        >
                          <LuArrowLeftRight size={14} />
                          Settle Up
                        </Link>
                      </Button>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </>
        )}
      </Card.Body>
    </Card.Root>
  );
}

// ── Filter Bar ────────────────────────────────────────────────────────

function FilterBar({
  friends,
  ctx,
  filters,
  onFiltersChange,
}: {
  friends: { userId: string }[];
  ctx: UserLookupContext;
  filters: {
    friendUserId: string;
    dateFrom: string;
    dateTo: string;
    currency: string;
    keywordInput: string;
    sortBy: ActivitySortBy;
  };
  onFiltersChange: (
    patch: Partial<{
      friendUserId: string;
      dateFrom: string;
      dateTo: string;
      currency: string;
      keywordInput: string;
      sortBy: ActivitySortBy;
    }>,
  ) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const advancedFilterCount = [
    filters.friendUserId,
    filters.currency,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length;

  const hasAnyActive = advancedFilterCount > 0 || filters.keywordInput;

  const clearAll = () => {
    onFiltersChange({ friendUserId: "", dateFrom: "", dateTo: "", currency: "", keywordInput: "" });
    setExpanded(false);
  };

  return (
    <Box px={{ base: "3", md: "0" }} mb="3">
      {/* Primary row: search + sort + filter toggle */}
      <HStack gap="2">
        <Input
          size="sm"
          placeholder="Search description..."
          value={filters.keywordInput}
          onChange={(e) => onFiltersChange({ keywordInput: e.target.value })}
          flex="1"
        />
        <NativeSelect.Root size="sm" width="auto" minW="150px" flexShrink={0}>
          <NativeSelect.Field
            aria-label="Sort by"
            value={filters.sortBy}
            onChange={(e) => onFiltersChange({ sortBy: e.target.value as ActivitySortBy })}
          >
            <option value="date">Transaction date</option>
            <option value="created_at">Date added</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        <Button
          size="sm"
          variant={expanded || advancedFilterCount > 0 ? "solid" : "outline"}
          colorPalette={advancedFilterCount > 0 ? "blue" : "gray"}
          onClick={() => setExpanded((v) => !v)}
          flexShrink={0}
        >
          <LuSlidersHorizontal />
          Filters
          {advancedFilterCount > 0 && (
            <Badge size="xs" variant="solid" colorPalette="blue" borderRadius="full" ml="1">
              {advancedFilterCount}
            </Badge>
          )}
        </Button>
        {hasAnyActive && (
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Clear all filters"
            onClick={clearAll}
            flexShrink={0}
          >
            <LuX />
          </IconButton>
        )}
      </HStack>

      {/* Expandable advanced filters */}
      {expanded && (
        <Box mt="2" p="3" borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
          <Flex gap="2" wrap="wrap" align="center">
            <NativeSelect.Root size="sm" minW="130px" flex="1">
              <NativeSelect.Field
                value={filters.friendUserId}
                onChange={(e) => onFiltersChange({ friendUserId: e.target.value })}
              >
                <option value="">All friends</option>
                {friends.map((f) => (
                  <option key={f.userId} value={f.userId}>
                    {getDisplayName(f.userId, ctx)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <NativeSelect.Root size="sm" minW="120px" flex="1">
              <NativeSelect.Field
                value={filters.currency}
                onChange={(e) => onFiltersChange({ currency: e.target.value })}
              >
                <option value="">All currencies</option>
                {SUPPORTED_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <Input
              size="sm"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onFiltersChange({ dateFrom: e.target.value })}
              flex="1"
              minW="140px"
            />

            <Input
              size="sm"
              type="date"
              value={filters.dateTo}
              onChange={(e) => onFiltersChange({ dateTo: e.target.value })}
              flex="1"
              minW="140px"
            />
          </Flex>
        </Box>
      )}
    </Box>
  );
}

// ── Activity List with infinite scroll ─────────────────────────────────

function ActivityList({
  items,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  renderItem,
  emptyState,
}: {
  items: ActivityItem[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  renderItem: (item: ActivityItem, index: number) => React.ReactNode;
  emptyState: React.ReactNode;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver to trigger loading more when sentinel is visible
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (items.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <>
      {items.map((item, index) => (
        <div key={`${item.type}-${item.item.id}`}>{renderItem(item, index)}</div>
      ))}
      <div ref={sentinelRef}>
        {isFetchingNextPage ? (
          <Flex justify="center" py="4">
            <Spinner size="sm" />
          </Flex>
        ) : hasNextPage ? (
          <Flex justify="center" py="4">
            <Text fontSize="sm" color="fg.muted">
              Loading more...
            </Text>
          </Flex>
        ) : items.length > 0 ? (
          <Text textAlign="center" py="4" fontSize="sm" color="fg.muted">
            All items loaded
          </Text>
        ) : null}
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────

function Activity() {
  const loaderData = Route.useLoaderData();
  const { balances, totals, friends, currentUser } = loaderData;
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ctx = { currentUser, friends };
  const [activeTab, setActiveTab] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    description: string;
  } | null>(null);
  const [deleteSettlementTarget, setDeleteSettlementTarget] = useState<{
    id: string;
    description: string;
  } | null>(null);

  // ── Filter state ──────────────────────────────────────────────────

  const [filterState, setFilterState] = useState({
    friendUserId: "",
    dateFrom: "",
    dateTo: "",
    currency: "",
    keywordInput: "",
    sortBy: "date" as ActivitySortBy,
  });

  // Debounce keyword so we don't fire on every keystroke
  const [keyword, setKeyword] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setKeyword(filterState.keywordInput), 300);
    return () => clearTimeout(t);
  }, [filterState.keywordInput]);

  const activeFilters = useMemo<ActivityFilters>(() => {
    const f: ActivityFilters = {};
    if (filterState.friendUserId) f.friendUserId = filterState.friendUserId;
    if (filterState.dateFrom) f.dateFrom = filterState.dateFrom;
    if (filterState.dateTo) f.dateTo = filterState.dateTo;
    if (filterState.currency) f.currency = filterState.currency;
    if (keyword) f.keyword = keyword;
    if (filterState.sortBy !== "date") f.sortBy = filterState.sortBy;
    return f;
  }, [
    filterState.friendUserId,
    filterState.dateFrom,
    filterState.dateTo,
    filterState.currency,
    filterState.sortBy,
    keyword,
  ]);

  const hasActiveFilters =
    Boolean(filterState.friendUserId) ||
    Boolean(filterState.dateFrom) ||
    Boolean(filterState.dateTo) ||
    Boolean(filterState.currency) ||
    Boolean(keyword) ||
    filterState.sortBy !== "date";

  const handleFiltersChange = useCallback((patch: Partial<typeof filterState>) => {
    setFilterState((prev) => ({ ...prev, ...patch }));
  }, []);

  // ── Infinite query ─────────────────────────────────────────────────

  const activityQuery = useInfiniteQuery({
    queryKey: ["activity", activeFilters],
    queryFn: ({ pageParam }) =>
      pageParam == null
        ? loadActivity({ data: { filters: activeFilters } }).then((d) => ({
            items: d.items,
            nextCursor: d.nextCursor,
          }))
        : loadMoreActivityFn({
            data: { cursor: pageParam, limit: PAGE_SIZE, filters: activeFilters },
          }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
    initialData: hasActiveFilters
      ? undefined
      : {
          pages: [
            {
              items: loaderData.items,
              nextCursor: loaderData.nextCursor,
            },
          ],
          pageParams: [null],
        },
  });

  const allItems = activityQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const expenseItems = allItems.filter(
    (a): a is ActivityItem & { type: "expense" } => a.type === "expense",
  );
  const settlementItems = allItems.filter(
    (a): a is ActivityItem & { type: "settlement" } => a.type === "settlement",
  );

  const fetchNext = useCallback(() => {
    activityQuery.fetchNextPage();
  }, [activityQuery]);

  // ── Delete handlers ───────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteExpenseFn({ data: { id: deleteTarget.id } });
      setDeleteTarget(null);
      queryClient.removeQueries({ queryKey: ["activity"] });
      router.invalidate();
    } catch (error) {
      showErrorToast("Failed to delete expense", error);
    }
  };

  const handleDeleteSettlement = async () => {
    if (!deleteSettlementTarget) return;
    try {
      await deleteSettlementFn({ data: { id: deleteSettlementTarget.id } });
      setDeleteSettlementTarget(null);
      queryClient.removeQueries({ queryKey: ["activity"] });
      router.invalidate();
    } catch (error) {
      showErrorToast("Failed to delete settlement", error);
    }
  };

  // ── Item renderers ─────────────────────────────────────────────────

  const renderExpenseItem = (expense: ExpenseView) => (
    <Box
      px="4"
      py="2"
      cursor="pointer"
      _hover={{ bg: "bg.subtle" }}
      borderBottomWidth="1px"
      borderColor="border"
      onClick={() => navigate({ to: "/expenses/$id", params: { id: expense.id } })}
    >
      <Flex justify="space-between" align="center" gap="3">
        <Box flex="1" minW="0">
          <Text fontWeight="medium" fontSize="sm" truncate>
            {expense.description}
          </Text>
          <HStack gap="2" mt="0.5">
            <Text fontSize="xs" color="fg.muted">
              {formatDate(expense.date)}
            </Text>
            {expense.splits && expense.splits.length > 0 && (
              <AvatarGroup size="2xs">
                {expense.splits.slice(0, 3).map((split) => {
                  const user = getUser(split.userId, ctx);
                  const displayName = getDisplayName(split.userId, ctx);
                  const identifier = user.email || split.userId;
                  return (
                    <Tooltip key={split.userId} content={displayName}>
                      <UserAvatar name={user.name} identifier={identifier} size="2xs" />
                    </Tooltip>
                  );
                })}
                {expense.splits.length > 3 && (
                  <Avatar.Root size="2xs">
                    <Avatar.Fallback>+{expense.splits.length - 3}</Avatar.Fallback>
                  </Avatar.Root>
                )}
              </AvatarGroup>
            )}
          </HStack>
        </Box>
        <HStack gap="1" flexShrink={0}>
          <Text fontWeight="medium" fontSize="sm" whiteSpace="nowrap">
            {formatAmount(expense.amount, expense.currency, { absolute: false })}
          </Text>
          <Tooltip content="Delete expense">
            <IconButton
              size="2xs"
              variant="ghost"
              colorPalette="red"
              aria-label="Delete expense"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget({ id: expense.id, description: expense.description });
              }}
            >
              <LuTrash2 size={13} />
            </IconButton>
          </Tooltip>
        </HStack>
      </Flex>
    </Box>
  );

  const renderSettlementItem = (s: SettlementView) => (
    <Box
      px="4"
      py="2"
      cursor="pointer"
      _hover={{ bg: "bg.subtle" }}
      borderBottomWidth="1px"
      borderColor="border"
      onClick={() => navigate({ to: "/settlements/$id", params: { id: s.id } })}
    >
      <Flex justify="space-between" align="center" gap="3">
        <Box flex="1" minW="0">
          <Text fontWeight="medium" fontSize="sm" truncate>
            {getDisplayName(s.fromUserId, ctx)} paid {getDisplayName(s.toUserId, ctx)}
          </Text>
          <HStack gap="2" mt="0.5">
            <Text fontSize="xs" color="fg.muted">
              {formatDate(s.date)}
            </Text>
            <Badge size="xs" variant="subtle" colorPalette="gray">
              <LuArrowLeftRight size={10} />
              Settlement
            </Badge>
          </HStack>
        </Box>
        <HStack gap="1" flexShrink={0}>
          <Text fontWeight="medium" fontSize="sm" whiteSpace="nowrap">
            {formatAmount(s.amount, s.currency, { absolute: false })}
          </Text>
          <Tooltip content="Delete settlement">
            <IconButton
              size="2xs"
              variant="ghost"
              colorPalette="red"
              aria-label="Delete settlement"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteSettlementTarget({
                  id: s.id,
                  description: `${getDisplayName(s.fromUserId, ctx)} paid ${getDisplayName(s.toUserId, ctx)}`,
                });
              }}
            >
              <LuTrash2 size={13} />
            </IconButton>
          </Tooltip>
        </HStack>
      </Flex>
    </Box>
  );

  const renderActivityItem = (activity: ActivityItem) => {
    if (activity.type === "expense") {
      return renderExpenseItem(activity.item);
    }
    return renderSettlementItem(activity.item);
  };

  // ── Empty states ────────────────────────────────────────────────────

  const filteredEmptyState = (
    <VStack align="center" gap="4" py="16" px="4">
      <Text fontSize="lg" color="fg.muted">
        No results found
      </Text>
      <Text fontSize="sm" color="fg.muted">
        Try adjusting your filters
      </Text>
    </VStack>
  );

  const allEmptyState = hasActiveFilters ? (
    filteredEmptyState
  ) : (
    <VStack align="center" gap="4" py="16" px="4">
      <Text fontSize="lg" color="fg.muted">
        No activity yet
      </Text>
      <Text fontSize="sm" color="fg.muted">
        Get started by adding your first expense
      </Text>
      <Button asChild>
        <Link to="/expenses/new">
          <LuPlus size={16} />
          Add Expense
        </Link>
      </Button>
    </VStack>
  );

  const expensesEmptyState = hasActiveFilters ? (
    filteredEmptyState
  ) : (
    <VStack align="center" gap="4" py="16" px="4">
      <Text fontSize="lg" color="fg.muted">
        No expenses yet
      </Text>
      <Text fontSize="sm" color="fg.muted">
        Get started by adding your first expense
      </Text>
      <Button asChild>
        <Link to="/expenses/new">
          <LuPlus size={16} />
          Add Expense
        </Link>
      </Button>
    </VStack>
  );

  const settlementsEmptyState = hasActiveFilters ? (
    filteredEmptyState
  ) : (
    <VStack align="center" gap="4" py="16" px="4">
      <Text fontSize="lg" color="fg.muted">
        No settlements yet
      </Text>
      <Text fontSize="sm" color="fg.muted">
        Record a payment to settle up with a friend
      </Text>
      <Button asChild>
        <Link to="/settlements/new">
          <LuPlus size={16} />
          Record Settlement
        </Link>
      </Button>
    </VStack>
  );

  return (
    <Container maxW="4xl" px={{ base: "0", md: "4" }}>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap="3"
        mb="4"
        px={{ base: "4", md: "0" }}
      >
        <Heading as="h2" size="lg">
          Activity
        </Heading>
        {/* Mobile: primary action + overflow menu for secondary actions */}
        <HStack gap="2" hideFrom="md">
          <AddExpenseButton size="sm" />
          <MenuRoot>
            <MenuTrigger asChild>
              <IconButton aria-label="More actions" variant="outline" size="sm">
                <LuEllipsis />
              </IconButton>
            </MenuTrigger>
            <MenuContent>
              <MenuItem value="settle" asChild>
                <Link to="/settlements/new">
                  <LuArrowLeftRight size={16} />
                  Settle Up
                </Link>
              </MenuItem>
            </MenuContent>
          </MenuRoot>
        </HStack>

        {/* Desktop: add expense split button + settle up */}
        <Flex gap="2" hideBelow="md">
          <AddExpenseButton size="sm" />
          <Button asChild variant="outline" size="sm">
            <Link to="/settlements/new">
              <LuArrowLeftRight size={16} />
              Settle Up
            </Link>
          </Button>
        </Flex>
      </Flex>

      <Box px={{ base: "3", md: "0" }}>
        <BalancesSummary
          balances={balances}
          totals={totals}
          currentUserId={currentUser.id}
          ctx={ctx}
        />
      </Box>

      <FilterBar
        friends={friends}
        ctx={ctx}
        filters={filterState}
        onFiltersChange={handleFiltersChange}
      />

      <Tabs.Root value={activeTab} onValueChange={(e) => setActiveTab(e.value)} variant="line">
        <Box px={{ base: "3", md: "0" }}>
          <Tabs.List>
            <Tabs.Trigger value="all">All</Tabs.Trigger>
            <Tabs.Trigger value="expenses">
              <LuReceipt size={14} />
              Expenses
            </Tabs.Trigger>
            <Tabs.Trigger value="settlements">
              <LuArrowLeftRight size={14} />
              Settlements
            </Tabs.Trigger>
          </Tabs.List>
        </Box>

        <Card.Root variant="outline" mt="3">
          <Card.Body p="0">
            <Tabs.Content value="all" p="0">
              <ActivityList
                items={allItems}
                hasNextPage={activityQuery.hasNextPage}
                isFetchingNextPage={activityQuery.isFetchingNextPage}
                fetchNextPage={fetchNext}
                renderItem={(item) => renderActivityItem(item)}
                emptyState={allEmptyState}
              />
            </Tabs.Content>
            <Tabs.Content value="expenses" p="0">
              <ActivityList
                items={expenseItems}
                hasNextPage={activityQuery.hasNextPage}
                isFetchingNextPage={activityQuery.isFetchingNextPage}
                fetchNextPage={fetchNext}
                renderItem={(item) => renderExpenseItem(item.item as ExpenseView)}
                emptyState={expensesEmptyState}
              />
            </Tabs.Content>
            <Tabs.Content value="settlements" p="0">
              <ActivityList
                items={settlementItems}
                hasNextPage={activityQuery.hasNextPage}
                isFetchingNextPage={activityQuery.isFetchingNextPage}
                fetchNextPage={fetchNext}
                renderItem={(item) => renderSettlementItem(item.item as SettlementView)}
                emptyState={settlementsEmptyState}
              />
            </Tabs.Content>
          </Card.Body>
        </Card.Root>
      </Tabs.Root>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete expense"
        description={`Are you sure you want to delete "${deleteTarget?.description}"? This will reverse all associated ledger transfers.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />

      <ConfirmDialog
        open={deleteSettlementTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteSettlementTarget(null);
        }}
        title="Delete settlement"
        description={`Are you sure you want to delete the settlement "${deleteSettlementTarget?.description}"? This will reverse the associated ledger transfer.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteSettlement}
      />
    </Container>
  );
}
